import type { components } from '@heidi/schema/ml-scribe';
import { z } from 'zod';
import { parseToolCallDisplay, type ToolCallDisplay } from './tool-call-display';

export const TOOL_APPROVAL_REASON_MAX_LENGTH = 500;

const recordSchema = z.record(z.string(), z.unknown());

export const toolApprovalRequestPublicSchema = z.object({
  approval_id: z.string().min(1),
  agent_run_id: z.string().min(1),
  turn_id: z.string().nullable().optional(),
  created_at: z.string().min(1),
  tool_call_id: z.string().min(1),
  tool_name: z.string().min(1),
  tool_input: recordSchema,
  redacted_tool_input_keys: z.array(z.string()),
  approval_kind: z.string().default('default'),
  context: recordSchema.default({}),
});

export const pendingApprovalsResponseSchema = z.object({
  approvals: z.array(toolApprovalRequestPublicSchema),
});

export type ToolApprovalVerdict = 'approve' | 'deny';

/**
 * The keys ml-scribe sends (`project_tool_approval_context`), closed so a read of
 * any other key fails to compile. Card content is `presentation`: use `readPresentation`.
 */
export type ApprovalContext = {
  tool_call_display?: unknown;
  presentation?: unknown;
  draft?: unknown;
  connect_required?: unknown;
  rpa_skill_workflow?: unknown;
  target_id?: string;
  session_override_allowed?: boolean;
  timeout_allowed?: boolean;
  input_modification_allowed?: boolean;
};

export type ToolApprovalRequestPublic =
  components['schemas']['ToolApprovalRequestPublic'];

export interface PendingToolApproval {
  approvalId: string;
  agentRunId: string;
  turnId?: string | null;
  toolCallId: string;
  toolName: string;
  toolInput: Record<string, unknown>;
  redactedToolInputKeys: string[];
  createdAt: string;
  timeoutSeconds?: number | null;
  approvalKind: string;
  context: ApprovalContext;
  toolCallDisplay?: ToolCallDisplay;
}

export interface ApprovalDetailsProps {
  approval: PendingToolApproval;
  onModifiedInputChange: (input: Record<string, unknown> | null) => void;
}

export type ResolveToolApprovalRequest = Omit<
  components['schemas']['ResolveApprovalRequest'],
  'verdict' | 'reason' | 'override_scope'
> & {
  verdict: ToolApprovalVerdict;
  // `reason` and `override_scope` carry backend defaults ('' and 'once') and are
  // not set by the frontend, so they're omitted from the request type.
  reason?: string;
};

export type ResolveToolApprovalResponse =
  components['schemas']['ResolveApprovalResponse'];

export const normalizeToolApprovalPublic = (
  approval: z.infer<typeof toolApprovalRequestPublicSchema>
): PendingToolApproval => {
  const context = approval.context ?? {};
  const toolCallDisplay = parseToolCallDisplay(context.tool_call_display);

  return {
    approvalId: approval.approval_id,
    agentRunId: approval.agent_run_id,
    turnId: approval.turn_id,
    toolCallId: approval.tool_call_id,
    toolName: approval.tool_name,
    toolInput: approval.tool_input,
    redactedToolInputKeys: approval.redacted_tool_input_keys ?? [],
    createdAt: approval.created_at,
    approvalKind: approval.approval_kind ?? 'default',
    context,
    ...(toolCallDisplay && { toolCallDisplay }),
  };
};
